import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { useNoticeBannersStackContext } from '@sg/garnish';

export function useApplePayErrorHandling() {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const onApplePayError = useCallback(
    (_error?: unknown) => {
      addNoticeBanner({
        text: formatMessage(messages.applePayError),
        palette: 'caution',
      });
    },
    [addNoticeBanner, formatMessage],
  );

  const onApplePayCancellation = useCallback(() => {
    // ────────────────────────────────────────────────────────────────────────────
    // If you're on safari and you don't have a card in your Apple Wallet,
    // and your Apple Id for your MacOS is not also present on an iPhone,
    // then Stripe automatically cancels the request without any feedback.
    // If you're on iOS or on a MacOS with the same Apple Id as an iPhone,
    // you'd get a message from Apple telling you to add a card to your wallet.
    // Therefore we have this banner here just for web to cover this edge case.
    // ────────────────────────────────────────────────────────────────────────────

    if (Platform.OS === 'web') {
      addNoticeBanner({
        text: formatMessage(messages.applePayCanceled),
        palette: 'caution',
      });
    }
  }, [addNoticeBanner, formatMessage]);

  return {
    onApplePayError,
    onApplePayCancellation,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  applePayError: {
    defaultMessage: 'Something went wrong with your Apple Pay request.',
    description: 'Apple Pay | Error',
  },
  applePayCanceled: {
    defaultMessage:
      'This Apple Pay transaction was canceled. Please check your Apple Wallet and try again.',
    description: 'Apple Pay | Canceled',
  },
});
